const RECEIPT_BOUND_APPLICATION_SUPPLEMENT_SCHEMA_VERSION =
  'cogo.receipt-bound-application-supplement.v1' as const

const NATIVE_APPLICATION_KEYS = new Set([
  'schemaVersion',
  'manifest',
  'blocks',
  'flows',
  'tokens',
  'metadata',
  'ontology',
  'ontologyReleaseSelection',
])

const NATIVE_BLOCK_KEYS = new Set([
  'meta',
  'ui',
  'events',
  'flow',
  'state',
  'data',
  // LoadedApp compatibility aliases are reconstructed from the native
  // partitions and must never be supplied by the supplement.
  'schemaVersion',
  'blockType',
  'root',
  'variables',
  'datasource',
  'manifest',
])

const NATIVE_BLOCK_UI_KEYS = new Set(['schemaVersion', 'blockType', 'root'])

type ReceiptBoundBlockSupplement = {
  block?: Record<string, unknown>
  ui?: Record<string, unknown>
}

export type ReceiptBoundApplicationSupplement = {
  schemaVersion: typeof RECEIPT_BOUND_APPLICATION_SUPPLEMENT_SCHEMA_VERSION
  application: Record<string, unknown>
  blocks: Record<string, ReceiptBoundBlockSupplement>
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function supplementalEntries(
  value: unknown,
  nativeKeys: ReadonlySet<string>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(readRecord(value)).filter(
      ([key, child]) => !nativeKeys.has(key) && child !== undefined,
    ),
  )
}

/**
 * The Studio DB has native columns for manifest, blocks, flows, tokens, and
 * the ontology/metadata settings projection. A reviewed application can also
 * contain hash-bound extension partitions (for example themes, validation,
 * appData, block metadata, or ui.meta). Persist only those non-native fields
 * in this supplement so reconnect can reconstruct the exact reviewed artifact
 * without allowing the supplement to replace any native DB authority.
 */
export function buildReceiptBoundApplicationSupplement(
  applicationValue: unknown,
): ReceiptBoundApplicationSupplement | null {
  const application = readRecord(applicationValue)
  const applicationSupplement = supplementalEntries(
    application,
    NATIVE_APPLICATION_KEYS,
  )
  const blockSupplementEntries: Array<[string, ReceiptBoundBlockSupplement]> =
    []

  for (const [blockName, blockValue] of Object.entries(
    readRecord(application.blocks),
  )) {
    const block = readRecord(blockValue)
    const blockSupplement = supplementalEntries(block, NATIVE_BLOCK_KEYS)
    const uiSupplement = supplementalEntries(block.ui, NATIVE_BLOCK_UI_KEYS)
    if (
      Object.keys(blockSupplement).length > 0 ||
      Object.keys(uiSupplement).length > 0
    ) {
      blockSupplementEntries.push([
        blockName,
        {
          ...(Object.keys(blockSupplement).length > 0
            ? { block: blockSupplement }
            : {}),
          ...(Object.keys(uiSupplement).length > 0 ? { ui: uiSupplement } : {}),
        },
      ])
    }
  }

  if (
    Object.keys(applicationSupplement).length === 0 &&
    blockSupplementEntries.length === 0
  ) {
    return null
  }

  return {
    schemaVersion: RECEIPT_BOUND_APPLICATION_SUPPLEMENT_SCHEMA_VERSION,
    application: applicationSupplement,
    blocks: Object.fromEntries(blockSupplementEntries),
  }
}

function readReceiptBoundApplicationSupplement(
  value: unknown,
): ReceiptBoundApplicationSupplement | null {
  const supplement = readRecord(value)
  if (
    supplement.schemaVersion !==
    RECEIPT_BOUND_APPLICATION_SUPPLEMENT_SCHEMA_VERSION
  ) {
    return null
  }
  return {
    schemaVersion: RECEIPT_BOUND_APPLICATION_SUPPLEMENT_SCHEMA_VERSION,
    application: supplementalEntries(
      supplement.application,
      NATIVE_APPLICATION_KEYS,
    ),
    blocks: Object.fromEntries(
      Object.entries(readRecord(supplement.blocks)).map(
        ([blockName, blockValue]) => {
          const block = readRecord(blockValue)
          return [
            blockName,
            {
              block: supplementalEntries(block.block, NATIVE_BLOCK_KEYS),
              ui: supplementalEntries(block.ui, NATIVE_BLOCK_UI_KEYS),
            },
          ]
        },
      ),
    ),
  }
}

export function receiptBoundApplicationSupplement(
  value: unknown,
): Record<string, unknown> {
  return readReceiptBoundApplicationSupplement(value)?.application ?? {}
}

export function receiptBoundBlockSupplement(
  value: unknown,
  blockName: string,
): ReceiptBoundBlockSupplement {
  return readReceiptBoundApplicationSupplement(value)?.blocks[blockName] ?? {}
}
